import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateScheduledDataWorkflowOptions,
  GetScheduledDataWorkflowOptions,
  JobsCreatedEnvelope,
  JobsDeletedEnvelope,
  JobsUpdatedEnvelope,
  ListScheduledDataWorkflowsOptions,
  ListScheduledDataWorkflowsResponse,
  RescheduleScheduledDataWorkflowOptions,
  RescheduleScheduledDataWorkflowResponse,
  ScheduledDataWorkflow,
  UpdateScheduledDataWorkflowOptions,
  createScheduledDataWorkflow as universalCreateScheduledDataWorkflow,
  deleteScheduledDataWorkflow as universalDeleteScheduledDataWorkflow,
  getScheduledDataWorkflow as universalGetScheduledDataWorkflow,
  listScheduledDataWorkflows as universalListScheduledDataWorkflows,
  rescheduleScheduledDataWorkflow as universalRescheduleScheduledDataWorkflow,
  triggerScheduledDataWorkflowNow as universalTriggerScheduledDataWorkflowNow,
  updateScheduledDataWorkflow as universalUpdateScheduledDataWorkflow,
} from './data-sync-v1-jobs-data-sync-jobs.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function createScheduledDataWorkflow(
  httpClient: HttpClient
): CreateScheduledDataWorkflowSignature {
  return (
    scheduledDataWorkflow: NonNullablePaths<
      ScheduledDataWorkflow,
      `_id` | `workflow` | `workflow.steps`,
      3
    >,
    options?: CreateScheduledDataWorkflowOptions
  ) =>
    universalCreateScheduledDataWorkflow(
      scheduledDataWorkflow,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateScheduledDataWorkflowSignature {
  /**
   * Creates a scheduled data workflow with a cron schedule.
   *
   * The scheduled data workflow automatically runs according to the specified cron expression.
   * Each execution creates a new [data workflow](https://dev.wix.com/docs/api-reference/business-solutions/cms/operations/data-movement-jobs/introduction) with its `scheduleId` set to this scheduled data workflow's ID.
   * @param - Scheduled data movement workflow to create.
   * @returns Created scheduled data movement workflow.
   */
  (
    scheduledDataWorkflow: NonNullablePaths<
      ScheduledDataWorkflow,
      `_id` | `workflow` | `workflow.steps`,
      3
    >,
    options?: CreateScheduledDataWorkflowOptions
  ): Promise<
    NonNullablePaths<
      ScheduledDataWorkflow,
      | `_id`
      | `name`
      | `enabled`
      | `cronExpression`
      | `workflow.steps`
      | `workflow.steps.${number}.sourceSiteId`
      | `workflow.steps.${number}.destinationSiteId`
      | `workflow.steps.${number}.source.wixDataCollection.collectionId`
      | `workflow.steps.${number}.source.file.format`
      | `workflow.steps.${number}.source.file.fileId`
      | `workflow.steps.${number}.source.file.url`
      | `workflow.steps.${number}.destination.wixDataCollection.collectionId`
      | `workflow.steps.${number}.destination.wixDataCollection.writePolicy`
      | `workflow.steps.${number}.destination.file.format`
      | `workflow.steps.${number}.destination.file.url`
      | `lastWorkflowId`,
      7
    >
  >;
}

export function rescheduleScheduledDataWorkflow(
  httpClient: HttpClient
): RescheduleScheduledDataWorkflowSignature {
  return (
    scheduledDataWorkflowId: string,
    options?: RescheduleScheduledDataWorkflowOptions
  ) =>
    universalRescheduleScheduledDataWorkflow(
      scheduledDataWorkflowId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RescheduleScheduledDataWorkflowSignature {
  /**
   * Reinitializes a scheduled data workflow based on the current cron expression.
   *
   * This is needed in case an [updated](https://dev.wix.com/docs/api-reference/business-solutions/cms/operations/data-sync-jobs/update-data-sync-job) scheduled data workflow gets out of sync.
   * For example, if you update the cron expression, it may not initially run according to the new expression.
   * This reschedules it to run according to the new expression.
   *
   * Only applies to `enabled` scheduled data workflows.
   * @param - ID of the scheduled data movement workflow to reschedule.
   */
  (
    scheduledDataWorkflowId: string,
    options?: RescheduleScheduledDataWorkflowOptions
  ): Promise<RescheduleScheduledDataWorkflowResponse>;
}

export function triggerScheduledDataWorkflowNow(
  httpClient: HttpClient
): TriggerScheduledDataWorkflowNowSignature {
  return (scheduledDataWorkflowId: string) =>
    universalTriggerScheduledDataWorkflowNow(
      scheduledDataWorkflowId,
      // @ts-ignore
      { httpClient }
    );
}

interface TriggerScheduledDataWorkflowNowSignature {
  /**
   * Enqueues an immediate execution for a scheduled data workflow.
   *
   * This custom action doesn't change the cron schedule.
   * It triggers the same data workflow execution path used by scheduled
   * TimeCapsule runs.
   * @param - ID of the scheduled data movement workflow to run immediately.
   */
  (scheduledDataWorkflowId: string): Promise<void>;
}

export function updateScheduledDataWorkflow(
  httpClient: HttpClient
): UpdateScheduledDataWorkflowSignature {
  return (
    scheduledDataWorkflow: NonNullablePaths<
      ScheduledDataWorkflow,
      `workflow` | `workflow.steps`,
      3
    >,
    options?: UpdateScheduledDataWorkflowOptions
  ) =>
    universalUpdateScheduledDataWorkflow(
      scheduledDataWorkflow,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateScheduledDataWorkflowSignature {
  /**
   * Updates an existing scheduled data workflow.
   * @param - Scheduled data movement workflow information to update.
   * @returns Updated scheduled data movement workflow.
   */
  (
    scheduledDataWorkflow: NonNullablePaths<
      ScheduledDataWorkflow,
      `workflow` | `workflow.steps`,
      3
    >,
    options?: UpdateScheduledDataWorkflowOptions
  ): Promise<
    NonNullablePaths<
      ScheduledDataWorkflow,
      | `_id`
      | `name`
      | `enabled`
      | `cronExpression`
      | `workflow.steps`
      | `workflow.steps.${number}.sourceSiteId`
      | `workflow.steps.${number}.destinationSiteId`
      | `workflow.steps.${number}.source.wixDataCollection.collectionId`
      | `workflow.steps.${number}.source.file.format`
      | `workflow.steps.${number}.source.file.fileId`
      | `workflow.steps.${number}.source.file.url`
      | `workflow.steps.${number}.destination.wixDataCollection.collectionId`
      | `workflow.steps.${number}.destination.wixDataCollection.writePolicy`
      | `workflow.steps.${number}.destination.file.format`
      | `workflow.steps.${number}.destination.file.url`
      | `lastWorkflowId`,
      7
    >
  >;
}

export function deleteScheduledDataWorkflow(
  httpClient: HttpClient
): DeleteScheduledDataWorkflowSignature {
  return (scheduledDataWorkflowId: string) =>
    universalDeleteScheduledDataWorkflow(
      scheduledDataWorkflowId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteScheduledDataWorkflowSignature {
  /**
   * Deletes a scheduled data workflow.
   *
   * This permanently removes the schedule and prevents future executions. Any currently running data workflows triggered by the schedule continue to completion.
   * @param - ID of the scheduled data movement workflow to delete.
   */
  (scheduledDataWorkflowId: string): Promise<void>;
}

export function getScheduledDataWorkflow(
  httpClient: HttpClient
): GetScheduledDataWorkflowSignature {
  return (
    scheduledDataWorkflowId: string,
    options?: GetScheduledDataWorkflowOptions
  ) =>
    universalGetScheduledDataWorkflow(
      scheduledDataWorkflowId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetScheduledDataWorkflowSignature {
  /**
   * Retrieves a scheduled data workflow by ID.
   * @param - ID of the scheduled data movement workflow to retrieve.
   * @returns Retrieved scheduled data movement workflow.
   */
  (
    scheduledDataWorkflowId: string,
    options?: GetScheduledDataWorkflowOptions
  ): Promise<
    NonNullablePaths<
      ScheduledDataWorkflow,
      | `_id`
      | `name`
      | `enabled`
      | `cronExpression`
      | `workflow.steps`
      | `workflow.steps.${number}.sourceSiteId`
      | `workflow.steps.${number}.destinationSiteId`
      | `workflow.steps.${number}.source.wixDataCollection.collectionId`
      | `workflow.steps.${number}.source.file.format`
      | `workflow.steps.${number}.source.file.fileId`
      | `workflow.steps.${number}.source.file.url`
      | `workflow.steps.${number}.destination.wixDataCollection.collectionId`
      | `workflow.steps.${number}.destination.wixDataCollection.writePolicy`
      | `workflow.steps.${number}.destination.file.format`
      | `workflow.steps.${number}.destination.file.url`
      | `lastWorkflowId`,
      7
    >
  >;
}

export function listScheduledDataWorkflows(
  httpClient: HttpClient
): ListScheduledDataWorkflowsSignature {
  return (options?: ListScheduledDataWorkflowsOptions) =>
    universalListScheduledDataWorkflows(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListScheduledDataWorkflowsSignature {
  /**
   * Retrieves all scheduled data workflows for the current site.
   *
   * The current implementation returns both enabled and disabled schedules. Request `paging` and `filter` fields are accepted for forward compatibility but are not applied by the service.
   */
  (options?: ListScheduledDataWorkflowsOptions): Promise<
    NonNullablePaths<
      ListScheduledDataWorkflowsResponse,
      | `scheduledDataWorkflows`
      | `scheduledDataWorkflows.${number}._id`
      | `scheduledDataWorkflows.${number}.name`
      | `scheduledDataWorkflows.${number}.enabled`
      | `scheduledDataWorkflows.${number}.cronExpression`
      | `scheduledDataWorkflows.${number}.lastWorkflowId`,
      4
    >
  >;
}

export const onJobsCreated = EventDefinition(
  'wix.data.sync.v1.jobs_created',
  true,
  (event: JobsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.lastExecutionDate' },
            { path: 'entity.nextExecutionDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobsCreatedEnvelope>();
export const onJobsDeleted = EventDefinition(
  'wix.data.sync.v1.jobs_deleted',
  true,
  (event: JobsDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.lastExecutionDate' },
            { path: 'undefined.nextExecutionDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobsDeletedEnvelope>();
export const onJobsUpdated = EventDefinition(
  'wix.data.sync.v1.jobs_updated',
  true,
  (event: JobsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.lastExecutionDate' },
            { path: 'entity.nextExecutionDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreateScheduledDataWorkflowOptions,
  CreateScheduledDataWorkflowRequest,
  CreateScheduledDataWorkflowResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DataMovementJobLogs,
  DeleteScheduledDataWorkflowRequest,
  DeleteScheduledDataWorkflowResponse,
  Destination,
  DestinationDestinationOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExternalFileSource,
  Field,
  FieldType,
  Fields,
  FileDestination,
  FileFormat,
  FileSource,
  GetScheduledDataWorkflowOptions,
  GetScheduledDataWorkflowRequest,
  GetScheduledDataWorkflowResponse,
  GoogleSheetsSource,
  IdentificationData,
  IdentificationDataIdOneOf,
  JobSpec,
  JobSpecSiteOverrideOneOf,
  JobsCreatedEnvelope,
  JobsDeletedEnvelope,
  JobsUpdatedEnvelope,
  ListScheduledDataWorkflowsOptions,
  ListScheduledDataWorkflowsRequest,
  ListScheduledDataWorkflowsResponse,
  LocalizationDestination,
  LocalizationSource,
  MediaType,
  MessageEnvelope,
  RescheduleScheduledDataWorkflowOptions,
  RescheduleScheduledDataWorkflowRequest,
  RescheduleScheduledDataWorkflowResponse,
  RestoreInfo,
  ScheduledDataWorkflow,
  ScheduledDataWorkflowSpec,
  Schema,
  Source,
  SourceSourceOneOf,
  StoresCatalogInventoryDestination,
  StoresCatalogProductsDestination,
  TestDestination,
  TestSource,
  TestTransformation,
  Transformation,
  TransformationTransformationOneOf,
  TriggerScheduledDataWorkflowNowRequest,
  TriggerScheduledDataWorkflowNowResponse,
  UpdateScheduledDataWorkflowOptions,
  UpdateScheduledDataWorkflowRequest,
  UpdateScheduledDataWorkflowResponse,
  WebhookIdentityType,
  WixDataCollectionDefinitionDestination,
  WixDataCollectionDefinitionSource,
  WixDataDestination,
  WixDataDestinationWritePolicy,
  WixDataEnvironment,
  WixDataSource,
  WixMediaDestination,
  WixMediaDestinationWritePolicy,
  WixMediaSource,
  WritePolicy,
} from './data-sync-v1-jobs-data-sync-jobs.universal.js';
